import { GetStaticPaths, GetStaticPropsContext } from 'next'
import Head from 'next/head'
import { useEffect } from 'react'
import { GlobalProps, Layout, withLayoutProps } from 'slices/Layout'

import AboutAuthor from '@/components/AboutAuthor/AboutAuthor'
import AuthorAvatar from '@/components/AuthorAvatar/AuthorAvatar'
import { CustomImage } from '@/components/CustomImage/CustomImage'
import { BlogPostSeo } from '@/components/seo/BlogPostSeo'
import { Tag } from '@/shared/components/Tag/Tag'
import { GOOGLE_EVENTS, META_EVENTS, trackEvent, useMetaTrack } from '@/shared/analytics'
import { StyledBreadcrumbLink } from '@/shared/components/Breadcrumbs/BreadcrumbLink'
import { Breadcrumbs } from '@/shared/components/Breadcrumbs/Breadcrumbs'
import { pxToRem } from '@/shared/styles/themes'
import { ArticleRenderer } from '@/slices/blog/fragments/ArticleRenderer'
import { InlineTableOfContents } from '@/slices/blog/fragments/InlineTableOfContents'
import { ReadingProgressBar } from '@/slices/blog/fragments/ReadingProgressBar'
import { RelatedArticles } from '@/slices/blog/fragments/RelatedArticles'
import { TableOfContents } from '@/slices/blog/fragments/TableOfContents'
import { Article, getAllSlugs, getArticleBySlug, getRelatedArticles } from '@/slices/blog/mocks/articles'
import { getAuthorById, getDefaultAuthor } from '@/slices/blog/mocks/authors'
import { Box, Container, styled, Typography } from '@mui/material'

import type { Author } from '@/slices/blog/mocks/authors'

interface BlogPostProps extends GlobalProps {
  article: Article
  author: Author
  relatedArticles: Article[]
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const slug = context.params?.slug as string
  const article = getArticleBySlug(slug)

  if (!article) {
    return { notFound: true }
  }

  const author = article.authorId
    ? getAuthorById(article.authorId) || getDefaultAuthor()
    : getDefaultAuthor()

  const relatedArticles = getRelatedArticles(slug, 3)

  return withLayoutProps(context, {
    props: { article, author, relatedArticles },
  })
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = getAllSlugs()

  return {
    paths: slugs.map((slug) => ({
      params: { slug },
    })),
    fallback: false,
  }
}

export default function BlogPost({ article, author, relatedArticles, ...layoutProps }: BlogPostProps) {
  const trackMeta = useMetaTrack()
  const isLandingPage = article.contentType === 'landing-page'
  const heroSrc = article.heroImage || article.image

  useEffect(() => {
    trackEvent(GOOGLE_EVENTS.VIEW_ITEM, {
      item_id: article.slug,
      item_name: article.title,
      item_category: 'Blog',
    })
    trackMeta({
      event_name: META_EVENTS.VIEW_CONTENT,
      item_id: [article.slug],
      item_name: article.title,
      item_category: 'Blog',
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const formattedDate = new Date(article.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const renderHeader = () => (
    <BlogHeader>
      {article.category && (
        <CategoryLabel>{article.category}</CategoryLabel>
      )}
      <BlogTitle variant="h1">{article.title}</BlogTitle>
      <MetaRow>
        <AuthorAvatar author={author} size="small" showName={true} />
        <MetaRight>
          <MetaText>{formattedDate}</MetaText>
          {article.readTime && (
            <>
              <MetaDot />
              <MetaText>{article.readTime}</MetaText>
            </>
          )}
        </MetaRight>
      </MetaRow>
      {article.tags && article.tags.length > 0 && (
        <TagsRow>
          {article.tags.slice(0, 4).map((tag) => (
            <Tag key={tag} label={tag} size="mini" type="neutral" sx={{ backgroundColor: '#FFF5EE', fontWeight: 500 }} />
          ))}
        </TagsRow>
      )}
      <HeaderDivider />
    </BlogHeader>
  )

  return (
    <Layout {...layoutProps}>
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400..700;1,9..144,400..700&display=swap"
        />
      </Head>
      <BlogPostSeo article={article} author={author} />
      <ReadingProgressBar />
      <PageWrapper>
        <BreadcrumbWrapper>
          <BreadcrumbBar maxWidth="lg">
            <Breadcrumbs>
              <StyledBreadcrumbLink href="/">Home</StyledBreadcrumbLink>
              <StyledBreadcrumbLink href="/blog">Blog</StyledBreadcrumbLink>
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight={500}
                sx={{
                  maxWidth: { xs: 200, md: 400 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {article.title}
              </Typography>
            </Breadcrumbs>
          </BreadcrumbBar>
        </BreadcrumbWrapper>

        {heroSrc && (
          <HeroContainer maxWidth="lg">
            <HeroImageWrapper>
              <CustomImage
                src={heroSrc}
                alt={article.heroImageAlt || article.title}
                variant="horizontal"
                objectFit="cover"
                borderRadius={0}
                priority
              />
            </HeroImageWrapper>
          </HeroContainer>
        )}

        <StyledContainer maxWidth={false} $variant={isLandingPage ? 'landing' : 'post'}>
          {isLandingPage ? (
            <LandingPageLayout>
              <LandingPageContent>
                <BlogArticle>
                  {renderHeader()}

                  {article.blocks && article.blocks.length > 0 ? (
                    <ArticleRenderer article={article} />
                  ) : (
                    <Section>
                      <Paragraph>{article.excerpt}</Paragraph>
                    </Section>
                  )}
                </BlogArticle>

                <AboutAuthor author={author} />
                <RelatedArticles articles={relatedArticles} />
              </LandingPageContent>
              <LandingPageSidebar>
                <TableOfContents article={article} />
              </LandingPageSidebar>
            </LandingPageLayout>
          ) : (
            <>
              <BlogArticle>
                {renderHeader()}

                <InlineTableOfContents blocks={article.blocks} />

                {article.blocks && article.blocks.length > 0 ? (
                  <ArticleRenderer article={article} />
                ) : (
                  <Section>
                    <Paragraph>{article.excerpt}</Paragraph>
                  </Section>
                )}
              </BlogArticle>

              <AboutAuthor author={author} />
              <RelatedArticles articles={relatedArticles} />
            </>
          )}
        </StyledContainer>
      </PageWrapper>
    </Layout>
  )
}

const PageWrapper = styled(Box)({
  background: 'linear-gradient(180deg, #FFFAF6 0%, #FFF5EE 100%)',
  minHeight: '100vh',
})

const BlogArticle = styled('article')({
  fontFamily: '"Fraunces", "Lora", "Libre Baskerville", Georgia, serif',
  '& *': {
    fontFamily: 'inherit',
  },
})

const BreadcrumbWrapper = styled(Box)({
  backgroundColor: '#fff7ee',
})

const BreadcrumbBar = styled(Container)(({ theme }) => ({
  paddingTop: pxToRem(12),
  paddingBottom: pxToRem(8),

  [theme.breakpoints.up('md')]: {
    paddingTop: pxToRem(12),
  },
}))

// Hero image
const HeroContainer = styled(Container)(({ theme }) => ({
  paddingTop: pxToRem(16),

  [theme.breakpoints.up('md')]: {
    paddingTop: pxToRem(24),
  },
}))

const HeroImageWrapper = styled(Box)({
  borderRadius: pxToRem(16),
  overflow: 'hidden',
})

// Content container — narrow reading column for blog posts (~70 char measure
// at the new 19px serif body), wider for landing pages so the right-rail TOC
// fits alongside without crowding the prose.
const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== '$variant',
})<{ $variant: 'landing' | 'post' }>(({ theme, $variant }) => ({
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: $variant === 'landing' ? pxToRem(1080) : pxToRem(740),
  paddingLeft: pxToRem(16),
  paddingRight: pxToRem(16),
  paddingBottom: pxToRem(80),

  [theme.breakpoints.up('md')]: {
    paddingLeft: pxToRem(24),
    paddingRight: pxToRem(24),
    paddingBottom: pxToRem(100),
  },
}))

// Header
const BlogHeader = styled('header')(({ theme }) => ({
  marginTop: pxToRem(24),

  [theme.breakpoints.up('md')]: {
    marginTop: pxToRem(32),
    marginBottom: pxToRem(8),
  },
}))

const CategoryLabel = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(12),
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: theme.palette.custom.terracotta,
  marginBottom: pxToRem(12),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(13),
    marginBottom: pxToRem(16),
  },
}))

const BlogTitle = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(34),
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: pxToRem(16),
  lineHeight: 1.15,
  letterSpacing: '-0.01em',

  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(42),
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(52),
    marginBottom: pxToRem(20),
  },
}))

const MetaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(8),
  marginBottom: pxToRem(16),

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(20),
  },
}))

const MetaRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
})

const MetaText = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(13),
  fontWeight: 400,
  color: theme.palette.text.disabled,
}))

const MetaDot = styled('span')(({ theme }) => ({
  width: pxToRem(3),
  height: pxToRem(3),
  borderRadius: '50%',
  backgroundColor: theme.palette.text.disabled,
}))

const TagsRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: pxToRem(6),
  marginBottom: pxToRem(16),
})

const HeaderDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  marginTop: pxToRem(16),
  marginBottom: pxToRem(32),
  backgroundColor: theme.palette.divider,

  [theme.breakpoints.up('md')]: {
    marginTop: pxToRem(20),
    marginBottom: pxToRem(40),
  },
}))

// Sections
const Section = styled('section')({
  marginBottom: pxToRem(40),

  '&:last-child': {
    marginBottom: 0,
  },
})

const Paragraph = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(16),
  color: theme.palette.primary.main,
  lineHeight: 1.8,
  marginBottom: pxToRem(20),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
    marginBottom: pxToRem(24),
  },
}))

// Landing page layout
const LandingPageLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: pxToRem(48),

  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
  },
}))

const LandingPageContent = styled(Box)({
  flex: 1,
  minWidth: 0,
})

const LandingPageSidebar = styled(Box)(({ theme }) => ({
  width: pxToRem(260),
  flexShrink: 0,

  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}))
